/* Hallmark · macrostructure: Narrative Workflow · F4 Step Sequence knobs: numbering=01/02/03, layout=horizontal flow, connector=none · nav: N3 existing side-rail · theme: Operational Architect · pre-emit critique: P5 H5 E4 S5 R5 V5 · tokens: pass · responsive: pass */
import { useDeferredValue, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderTree,
  HelpCircle,
  Layers3,
  MessageSquare,
  Music,
  Search,
  Video,
} from 'lucide-react';
import { Button, LoadingSpinner } from '@lms/ui';
import { useMaterialLibrary } from '../hooks/useMaterials';
import { MaterialModal } from '../components/MaterialModal';
import type { Material, MaterialType } from '@lms/shared/types';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage, type Bilingual as BilingualType } from '../lib/LanguageContext';

const PAGE_SIZE = 24;

type CurriculumMaterial = Material & {
  session?: Material['session'] & {
    subject?: NonNullable<Material['session']>['subject'] & {
      stage?: {
        stageCode?: string;
        level?: { program?: { id: string; name: string; code: string } };
      };
    };
  };
};

function getMaterialContext(material: CurriculumMaterial) {
  const sessionProgram = material.session?.subject?.stage?.level?.program;
  const flowProgram = material.flow?.program;
  const program = sessionProgram ?? flowProgram;
  if (material.session?.subject) {
    const stageLabel =
      material.session.subject.stage?.name ?? material.session.subject.stage?.stageCode;
    return {
      programId: program?.id ?? '',
      programName: program?.name,
      primary: material.session.subject.name,
      secondary: [stageLabel, material.session.title].filter(Boolean).join(' · '),
      kind: 'session' as const,
    };
  }
  if (material.flow) {
    return {
      programId: material.flow.programId ?? program?.id ?? '',
      programName: program?.name,
      primary: material.flow.name,
      secondary: undefined,
      kind: 'flow' as const,
    };
  }
  return {
    programId: '',
    programName: undefined,
    primary: undefined,
    secondary: undefined,
    kind: 'none' as const,
  };
}

function getMaterialIcon(type: MaterialType) {
  if (type === 'VIDEO') return <Video size={20} />;
  if (type === 'AUDIO') return <Music size={20} />;
  if (type === 'DOCUMENT') return <FileText size={20} />;
  if (type === 'QUIZ') return <HelpCircle size={20} />;
  if (type === 'REFLECTION') return <BookOpen size={20} />;
  return <MessageSquare size={20} />;
}

export function MaterialsWorkspace() {
  const { t } = useLanguage();
  const [filterType, setFilterType] = useState<MaterialType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState<CurriculumMaterial | null>(null);
  const deferredSearch = useDeferredValue(searchQuery.trim());
  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(filterType === 'all' ? {} : { type: filterType }),
      ...(deferredSearch ? { search: deferredSearch } : {}),
    }),
    [deferredSearch, filterType, page]
  );
  const { data, isLoading, isFetching } = useMaterialLibrary(filters);
  const materials = (data?.items ?? []) as CurriculumMaterial[];
  const meta = data?.meta;
  const totalPages = meta?.totalPages ?? 1;
  const materialTypes: { value: MaterialType | 'all'; label: BilingualType }[] = [
    { value: 'all', label: copy.materials.filterAll },
    { value: 'VIDEO', label: copy.materials.filterVideo },
    { value: 'AUDIO', label: copy.materials.filterAudio },
    { value: 'DOCUMENT', label: copy.materials.filterDocument },
    { value: 'QUIZ', label: copy.materials.filterQuiz },
    { value: 'REFLECTION', label: copy.materials.typeReflection },
    { value: 'DISCUSSION', label: copy.materials.typeDiscussion },
  ];

  if (isLoading)
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );

  return (
    <div className="mx-auto max-w-[1440px] space-y-10 pb-14 animate-fadeIn">
      <section className="grid gap-8 rounded-[2rem] bg-surface-container-low p-6 shadow-level1 sm:p-8 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-end">
        <div className="min-w-0">
          <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
            <Bilingual {...copy.materials.workflowEyebrow} />
          </p>
          <h1 className="mt-3 max-w-3xl font-headline text-3xl font-bold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.materials.workflowTitle} />
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-on-surface-variant sm:text-base">
            <Bilingual {...copy.materials.workflowDesc} />
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row xl:flex-col">
          <Link
            to="/programs"
            className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-xs font-label font-bold text-on-primary no-underline shadow-level1 transition-colors duration-150 hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.98]"
          >
            <FolderTree size={17} />
            <Bilingual {...copy.materials.openPrograms} />
            <ArrowRight size={15} />
          </Link>
          <Link
            to="/materials/review"
            className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-surface-container-lowest px-5 text-xs font-label font-bold text-on-surface no-underline shadow-level1 transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.98]"
          >
            <HelpCircle size={17} />
            <Bilingual {...copy.materials.openReviewQueue} />
          </Link>
        </div>
      </section>

      <ol className="grid gap-4 md:grid-cols-3" aria-label={t(copy.materials.workflowEyebrow)}>
        {[
          {
            step: '01',
            title: copy.materials.workflowProgram,
            description: copy.materials.workflowProgramDesc,
            icon: <FolderTree size={20} />,
            to: '/programs',
          },
          {
            step: '02',
            title: copy.materials.workflowCurriculum,
            description: copy.materials.workflowCurriculumDesc,
            icon: <Layers3 size={20} />,
            to: '/programs',
          },
          {
            step: '03',
            title: copy.materials.workflowMaterials,
            description: copy.materials.workflowMaterialsDesc,
            icon: <BookOpen size={20} />,
          },
        ].map(({ step, title, description, icon, to }) => {
          const content = (
            <>
              <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                {icon}
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] font-label font-bold uppercase tracking-[0.17em] text-primary">
                  {step}
                </span>
                <span className="mt-1 block text-sm font-label font-bold text-on-surface">
                  <Bilingual {...title} />
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-on-surface-variant">
                  <Bilingual {...description} />
                </span>
              </span>
              {to && <ArrowRight size={16} className="ml-auto shrink-0 text-on-surface-variant" />}
            </>
          );
          return (
            <li key={step}>
              {to ? (
                <Link
                  to={to}
                  className="flex min-h-32 items-start gap-4 rounded-2xl bg-surface-container-low p-5 text-left no-underline shadow-level1 transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                >
                  {content}
                </Link>
              ) : (
                <div className="flex min-h-32 items-start gap-4 rounded-2xl bg-primary p-5 text-on-primary shadow-level2">
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <section className="space-y-6" aria-labelledby="material-library-title">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
              <Bilingual {...copy.materials.libraryEyebrow} />
            </p>
            <h2
              id="material-library-title"
              className="mt-2 font-headline text-2xl font-bold tracking-tight text-on-surface"
            >
              <Bilingual {...copy.materials.listTitle} />
            </h2>
          </div>
          <div className="rounded-xl bg-surface-container-low px-4 py-3 text-right">
            <p className="text-sm font-label font-bold text-on-surface">
              {t(copy.materials.libraryCount).replace('{count}', String(meta?.total ?? 0))}
            </p>
            <p className="mt-1 text-[11px] text-on-surface-variant">
              {t(copy.materials.libraryPage)
                .replace('{page}', String(meta?.page ?? page))
                .replace('{totalPages}', String(totalPages))}
            </p>
          </div>
        </div>
        <div className="grid gap-5 rounded-[1.75rem] bg-surface-container-low p-5 shadow-level1 sm:p-6 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="material-library-search"
                className="block text-xs font-label font-bold text-on-surface"
              >
                <Bilingual {...copy.materials.labelSearch} />
              </label>
              <p className="mt-1 text-[11px] text-on-surface-variant">
                <Bilingual {...copy.materials.searchHelper} />
              </p>
            </div>
            <div className="relative">
              <Search
                aria-hidden="true"
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
              />
              <input
                id="material-library-search"
                type="search"
                value={searchQuery}
                onChange={event => {
                  setSearchQuery(event.target.value);
                  setPage(1);
                }}
                placeholder={t(copy.materials.searchPlaceholder)}
                className="h-12 w-full rounded-xl bg-surface-container-lowest py-3 pl-11 pr-4 text-sm text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
              />
            </div>
          </div>
          <fieldset className="min-w-0">
            <legend className="mb-3 text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.materials.filterLabel} />
            </legend>
            <div className="flex flex-wrap gap-2">
              {materialTypes.map(({ value, label }) => {
                const active = filterType === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setFilterType(value);
                      setPage(1);
                    }}
                    aria-pressed={active}
                    className={`min-h-11 rounded-xl px-3 text-[10px] font-label font-bold uppercase tracking-[0.12em] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${active ? 'bg-primary text-on-primary shadow-level1' : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'}`}
                  >
                    <Bilingual {...label} />
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>
        {isFetching && (
          <p className="text-xs font-label font-bold text-primary" role="status">
            <Bilingual {...copy.materials.libraryUpdating} />
          </p>
        )}
        {materials.length > 0 ? (
          <div className="space-y-3">
            {materials.map(material => {
              const context = getMaterialContext(material);
              const typeLabel = materialTypes.find(option => option.value === material.type)?.label;
              return (
                <article
                  key={material.id}
                  className="grid gap-5 rounded-2xl bg-surface-container-lowest p-5 shadow-level1 transition-colors duration-150 hover:bg-surface-container-low sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:p-6"
                >
                  <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                    {getMaterialIcon(material.type)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[9px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                        <Bilingual {...(typeLabel ?? copy.materials.filterAll)} />
                      </span>
                      {material.isRequired && (
                        <span className="rounded-full bg-secondary/15 px-2.5 py-1 text-[9px] font-label font-bold uppercase tracking-[0.14em] text-on-secondary-container">
                          <Bilingual {...copy.materials.requiredBadge} />
                        </span>
                      )}
                    </div>
                    <h3 className="mt-3 text-base font-headline font-bold text-on-surface sm:text-lg">
                      {material.title}
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs leading-relaxed text-on-surface-variant">
                      {material.description || t(copy.materials.noContext)}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-on-surface-variant">
                      {context.programName && (
                        <span className="rounded-lg bg-surface-container-low px-2.5 py-1.5 font-label font-medium">
                          {context.programName}
                        </span>
                      )}
                      {context.primary ? (
                        <span className="rounded-lg bg-surface-container-low px-2.5 py-1.5 font-label font-medium">
                          {context.kind === 'session'
                            ? t(copy.materials.sessionLabel)
                            : t(copy.materials.flowLabel)}{' '}
                          · {context.primary}
                        </span>
                      ) : (
                        <span className="rounded-lg bg-error/10 px-2.5 py-1.5 font-label font-medium text-error">
                          <Bilingual {...copy.materials.noContext} />
                        </span>
                      )}
                      {context.secondary && (
                        <span className="rounded-lg bg-surface-container-low px-2.5 py-1.5 font-label font-medium">
                          {context.secondary}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedMaterial(material)}
                    className="h-11 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                  >
                    <Bilingual {...copy.materials.openMaterial} />
                    <ArrowRight size={15} />
                  </Button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-[2rem] bg-surface-container-low p-10 text-center shadow-level1 sm:p-16">
            <Layers3 size={34} className="mx-auto text-primary" />
            <h3 className="mt-4 font-headline text-xl font-bold text-on-surface">
              <Bilingual {...copy.materials.emptyFilteredTitle} />
            </h3>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.materials.emptyFilteredDesc} />
            </p>
            <button
              type="button"
              onClick={() => {
                setFilterType('all');
                setSearchQuery('');
                setPage(1);
              }}
              className="mt-6 min-h-11 rounded-xl bg-surface-container-lowest px-4 text-xs font-label font-bold text-primary shadow-level1 transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
            >
              <Bilingual {...copy.materials.clearFilters} />
            </button>
          </div>
        )}
        {totalPages > 1 && (
          <nav
            className="flex items-center justify-between gap-4 rounded-2xl bg-surface-container-low p-4"
            aria-label={t(copy.materials.libraryEyebrow)}
          >
            <button
              type="button"
              onClick={() => setPage(current => Math.max(1, current - 1))}
              disabled={page <= 1}
              aria-label={t(copy.materials.previousPage)}
              className="grid size-11 place-items-center rounded-xl bg-surface-container-lowest text-on-surface transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-45 active:scale-95"
            >
              <ChevronLeft size={18} />
            </button>
            <p className="text-xs font-label font-bold text-on-surface-variant">
              {t(copy.materials.libraryPage)
                .replace('{page}', String(page))
                .replace('{totalPages}', String(totalPages))}
            </p>
            <button
              type="button"
              onClick={() => setPage(current => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages}
              aria-label={t(copy.materials.nextPage)}
              className="grid size-11 place-items-center rounded-xl bg-surface-container-lowest text-on-surface transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-45 active:scale-95"
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </section>
      {selectedMaterial && (
        <MaterialModal
          material={selectedMaterial}
          flowId={selectedMaterial.flowId ?? ''}
          sessionId={selectedMaterial.sessionId}
          contextLabel={getMaterialContext(selectedMaterial).primary}
          programId={getMaterialContext(selectedMaterial).programId}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
    </div>
  );
}
